import { Injectable, inject } from '@angular/core';
import {
  CustomerEquipmentUpgradeAgreement,
  EquipmentUpgradeProductVersion,
  RedemptionStatus,
} from '../modules/mobilephone/models/equipment-upgrade-agreement.model';
import { DatePipe } from '@angular/common';
import { Store } from '@ngxs/store';
import { CustomerSelectors } from '../modules/details/states/customer.selectors';
import {
  GradingAssessmentRequest,
  ClaimRegistrationDetails,
  MobilBytteBytteRequest,
  RegisterMobilbytteBytteRequest,
} from '../models/mobilbytte-bytte.model';
import { isPhoneDamagedOptions } from '../modules/mobilephone/components/register-mobilbytte-dialog/register-mobilbytte-stepper/condition-step/condition-step.component';
import { RegisterMobilbytteFormModel } from '../modules/mobilephone/components/register-mobilbytte-dialog/register-mobilbytte-stepper/register-mobilbytte-stepper.component';
import { FormGroup } from '@angular/forms';
import { OrderSelectors } from '../states/order/order.selectors';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { TradeInSelectors } from '@app/sales/states/customer-product-trade-in/customer-product-trade-in.selectors';

export enum MobilBytteStatus {
  /**
   * Indicates that the device can be upgraded in the future,
   * but is not currently eligible for an upgrade.
   */
  CanBeUpgradedInTheFuture = 'CanBeUpgradedInTheFuture',

  /**
   * Indicates that the device is currently eligible for an upgrade.
   */
  IsUpgradableNow = 'IsUpgradableNow',

  /**
   * Indicates that there is only a down payment.
   * and the device cannot be upgraded.
   */
  FutureDownPaymentNoUpgrade = 'FutureDownPaymentNoUpgrade',

  /**
   * Indicates that the device is upgradable, but the customer
   * cannot proceed due to a low credit score.
   */
  UpgradableButLowCreditScore = 'UpgradableButLowCreditScore',
  /**
   * Indicates that the device is qualified for an upgrade, but
   * the customer needs to perform a detailed credit check.
   */
  QualifiedForUpgrade = 'QualifiedForUpgrade',
  /**
   * Indicates that the agreement has been terminated
   */
  Terminated = 'Terminated',
  /**
   * Indicates an active redemption is in progress for the agreement
   */
  RedemptionAlreadyInProgress = 'RedemptionAlreadyInProgress',

  /**
   * Indicates that there is a trade-in in the cart, which prevents mobilbytte bytte.
   */
  HasTradeinInCart = 'HasTradeinInCart',
}

export interface MobilBytteInfo {
  status: MobilBytteStatus;
  text: string;
}

@Injectable({
  providedIn: 'root',
})
export class MobilBytteHelperService {
  private store = inject(Store);

  getMobilBytteStatus(existingAgreement: CustomerEquipmentUpgradeAgreement): MobilBytteStatus {
    const hasPerformedDetailedCreditCheck = this.store.selectSnapshot(CustomerSelectors.hasPerformedDetailedCreditCheck);
    const hasTradeinInCart = this.store.selectSnapshot(TradeInSelectors.hasAcceptedTradeInVoucher);

    if (
      existingAgreement.redemptionStatus === RedemptionStatus.Scheduled ||
      existingAgreement.redemptionStatus === RedemptionStatus.PendingFinalGrading ||
      existingAgreement.redemptionStatus === RedemptionStatus.Completed
    ) {
      return MobilBytteStatus.RedemptionAlreadyInProgress;
    }

    if (hasTradeinInCart) {
      return MobilBytteStatus.HasTradeinInCart;
    }

    if (!!existingAgreement.terminatedDate) {
      return MobilBytteStatus.Terminated;
    }

    if (!existingAgreement.canBeUpgraded) {
      return MobilBytteStatus.FutureDownPaymentNoUpgrade;
    }

    const remainingPeriods = existingAgreement.remainingNumberOfPeriodsUntilUpgrade;

    if (hasPerformedDetailedCreditCheck) {
      const equipmentUpgradeAllowance = this.store.selectSnapshot(CustomerSelectors.equipmentUpgradeAllowance);

      if (remainingPeriods == 0) {
        return equipmentUpgradeAllowance.redemptionCreditDecision
          ? MobilBytteStatus.IsUpgradableNow
          : MobilBytteStatus.UpgradableButLowCreditScore;
      }
    } else if (remainingPeriods === 0) {
      return MobilBytteStatus.QualifiedForUpgrade;
    }

    if (remainingPeriods > 0) {
      return MobilBytteStatus.CanBeUpgradedInTheFuture;
    }
  }

  getMobilBytteStatusText(existingAgreement: CustomerEquipmentUpgradeAgreement): string {
    const datePipe = new DatePipe('nb');
    const mobilBytteStatus = this.getMobilBytteStatus(existingAgreement);
    switch (mobilBytteStatus) {
      case MobilBytteStatus.CanBeUpgradedInTheFuture:
        const formattedDate = datePipe.transform(existingAgreement.earliestEligibleUpgradeDate, 'dd.MM.yyyy');
        return `Kan byttes ${formattedDate}`;
      case MobilBytteStatus.FutureDownPaymentNoUpgrade:
        const formattedEndDate = datePipe.transform(existingAgreement.endDate, 'dd.MM.yyyy');
        return `Ferdig nedbetalt ${formattedEndDate}`;
      case MobilBytteStatus.IsUpgradableNow:
        return 'Kan byttes nå';
      case MobilBytteStatus.UpgradableButLowCreditScore:
      case MobilBytteStatus.HasTradeinInCart:
        return 'Bytte ikke tilgjengelig';
      case MobilBytteStatus.QualifiedForUpgrade:
        return 'Kan byttes nå (ved godkjent kredittsjekk)';
      case MobilBytteStatus.Terminated:
        return 'Avtalen er terminert';

      default:
        return '';
    }
  }

  getMobilBytteInfo(existingAgreement: CustomerEquipmentUpgradeAgreement): MobilBytteInfo {
    return {
      status: this.getMobilBytteStatus(existingAgreement),
      text: this.getMobilBytteStatusText(existingAgreement),
    };
  }

  isV2(existingAgreement: CustomerEquipmentUpgradeAgreement): boolean {
    return existingAgreement.productVersion === EquipmentUpgradeProductVersion.V2;
  }

  private createMobilBytteBytteRequest(equipmentUpgradeAgreementId: number): MobilBytteBytteRequest {
    const dealer = this.store.selectSnapshot(DealerStateSelectors.activeDealer);
    const orderServiceId = this.store.selectSnapshot(OrderSelectors.currentOrderServiceId);

    return {
      dealerId: dealer.dealerId,
      agreementId: equipmentUpgradeAgreementId,
      orderId: +orderServiceId,
    } as MobilBytteBytteRequest;
  }

  private createGradingAssessmentRequest(
    registerMobilbytteForm: FormGroup<RegisterMobilbytteFormModel>
  ): GradingAssessmentRequest {
    const { isPhoneDamaged, confirmationOfExternalDamage, dateOfDamage, explanationOfDamage, damageReasons } =
      registerMobilbytteForm.controls.conditionForm.controls;

    const claimRegistrationDetails = {
      customerConfirmsThatTheDamageWasCausedByAnExternalEvent: confirmationOfExternalDamage.value,
      incidentDate: !!dateOfDamage.value ? new Date(dateOfDamage.value) : null,
      damageReasonCodes: damageReasons.controls.map(reason => reason.value),
      description: explanationOfDamage.value,
    } as ClaimRegistrationDetails;

    return {
      isDamaged: isPhoneDamaged.value === isPhoneDamagedOptions.YES,
      claimRegistrationDetails: claimRegistrationDetails,
    } as GradingAssessmentRequest;
  }

  createRegisterMobilbytteRequest(
    registerMobilbytteForm: FormGroup<RegisterMobilbytteFormModel>,
    equipmentUpgradeAgreementId: number
  ): RegisterMobilbytteBytteRequest {
    return {
      createRedemption: this.createMobilBytteBytteRequest(equipmentUpgradeAgreementId),
      gradingAssessment: this.createGradingAssessmentRequest(registerMobilbytteForm),
    };
  }
}
